import { noop } from "lodash";
import { FC, useCallback, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ErrorText } from "../../../../../components/billing";
import { Button } from "../../../../../components/button";
import { Select } from "../../../../../components/forms/select";
import { TextField } from "../../../../../components/forms/text";
import { Flex, FullSize } from "../../../../../components/layout";
import { Loader } from "../../../../../components/loader";
import { useAppLoader } from "../../../../../components/loader/app-loader-hook";
import { BillingDTO } from "../../../../../store/billing/models/dto";
import { Billing } from "../../../../../store/billing/reducer";
import { BillingService } from "../../../../../store/billing/service";
import { ProductionBlockchains } from "../../../../../store/chain/models/internal";
import { useFetchable } from "../../../../../store/common/fetchable";
import { validationFields } from "../../../../../store/common/models/fields";
import { UsersDTO } from "../../../../../store/users/models/dto";
import { Users } from "../../../../../store/users/reducer";
import {
  mapObject,
  useYupValidationResolver,
} from "../../../../../utils/utils";

type FormProps = {
  chain?: BillingDTO.Responses.GetSKRTAddresses.T[number];
  amount: number;
};

const Form = styled.div`
  width: 100%;
  max-width: 30em;
`;

const StyledForm = styled(Flex).attrs({ as: "form" })`
  ${FullSize};
  flex-direction: column;
  justify-content: space-around;
  align-items: center;

  ${TextField}:not(:last-child) {
    margin-bottom: 1em;
  }
`;

export const Purchase: FC<
  {
    proceed: (purchaseID: string) => void;
  } & FormProps
> = (props) => {
  const forexRate = useFetchable(Billing.forexRate);
  const currentUser = useFetchable(Users.current);
  return (
    <Loader<
      BillingDTO.Responses.GetForexRate.T | UsersDTO.Responses.GetSingle.T
    >
      data={[forexRate, currentUser]}
    >
      {([loadedRate, loadedUser]) => (
        <InnerPurchase
          {...props}
          rate={(loadedRate as BillingDTO.Responses.GetForexRate.T).rate}
          kycStatus={(loadedUser as UsersDTO.Responses.GetSingle.T).kycStatus}
        />
      )}
    </Loader>
  );
};

export const InnerPurchase: FC<
  FormProps & {
    proceed: (purchaseID: string) => void;
    rate: BillingDTO.Responses.GetForexRate.T["rate"];
    kycStatus: UsersDTO.Responses.GetSingle.T["kycStatus"];
  }
> = ({ proceed, amount, kycStatus, chain }) => {
  const loader = useAppLoader();
  const resolver = useYupValidationResolver(
    validationFields.sourceWallet.useSchema()
  );

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<BillingDTO.Requests.Purchase.T["buyerWallet"]>({
    resolver,
    defaultValues: {
      address: "",
      chain: chain?.chain || "ETH",
    },
  });

  useEffect(() => {
    setValue("chain", chain?.chain || "ETH");
  }, [chain]);

  const onSubmit = useCallback(
    (buyerWallet: BillingDTO.Requests.Purchase.T["buyerWallet"]) =>
      loader(
        BillingService.purchase({
          buyerWallet,
          credits: amount,
          currency: "USDT",
        })
          .then(({ id }) => proceed(id))
          .catch(noop)
      ),
    [loader, amount]
  );

  const { t } = useTranslation("billing");

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <Form>
        <Controller
          control={control}
          name="address"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              error={!!error}
              helperText={error?.message}
              fullWidth
              label={t("source-address", { ns: "fields" })}
            />
          )}
        />
        <Controller
          control={control}
          name="chain"
          render={({ field }) => (
            <Select
              {...field}
              disabled
              fullWidth
              label={t("source-chain")}
              error={!!errors.chain}
              options={mapObject(ProductionBlockchains.enum, (value, key) => ({
                title: value,
                value: key,
              }))}
            />
          )}
        />
      </Form>
      <Button type="submit" disabled={kycStatus !== "VERIFIED"}>
        {t("start-purchase")}
      </Button>
      {kycStatus !== "VERIFIED" ? (
        <ErrorText>
          {t("you-must-verify-yourself-to-purchase-credits")}
        </ErrorText>
      ) : null}
    </StyledForm>
  );
};
